import { Accordion, AccordionTab } from 'primereact/accordion';
import { CheckoutConfirmation } from 'components/CheckoutConfirmation';
import { itemsAreAllVirtual } from 'utils/cartUtils';
import { ReactElement, useEffect, useState } from 'react';
import { useCartContext } from 'CartContext';
import { useNavigate } from 'react-router-dom';
import AccountAndBillingForm from 'components/forms/AccountAndBillingForm';
import CheckoutAccount from 'components/CheckoutAccount';
import CheckoutPayment from 'components/CheckoutPayment';
import CheckoutSummary from 'components/CheckoutSummary';
import Footer from 'components/footer-crwd/Footer';
import Nav from 'components/nav/Nav';
import ShippingForm from 'components/forms/ShippingForm';
import './Checkout.scss';
import buttonize from 'utils/buttonize';
import TagManager from 'react-gtm-module';
import { Head } from './head/Head';

export enum CustomerOption {
  guestCheckout,
  login,
  registerAccount,
}

export default function Checkout(): ReactElement {
  const checkoutOptionsIndex = 0;
  const accountBillingDetailsIndex = 1;
  const shippingDetailsIndex = 2;
  const paymentIndex = 3;
  const confirmationIndex = 4;

  const navigate = useNavigate();

  const [accordionIndex, setAccordionIndex] = useState(checkoutOptionsIndex);
  const [clickNeedsHandling, setClickNeedsHandling] = useState(false);
  const [newCustomerOption, setNewCustomerOption] = useState<number>(
    CustomerOption.login,
  );
  const [hasConfirmedBilling, setHasConfirmedBilling] = useState(false);
  const [localOrderNumber, setLocalOrderNumber] = useState<string>();
  const [orderCompleted, setOrderCompleted] = useState(false);

  const {
    billingAddress,
    items,
    loading,
    shippingAddress,
    userInfo,
    total,
    setBillingAddress,
    setOrderNumber,
  } = useCartContext();

  const ItemObject = items.map(item => ({
    name: item.product.name,
    id: item.product.sku,
    price: Number(item.product.price_range.maximum_price.final_price.value),
    brand: item.product.vendor_company,
    category: item.product.categories,
    variant: '',
    quantity: item.quantity,
  }));

  const purchaseURL =
    window.location !== window.parent.location
      ? document.referrer
      : window.location.hostname;

  const shouldGetShipping = !itemsAreAllVirtual(items);

  useEffect(() => {
    if (userInfo && !billingAddress) {
      setBillingAddress({
        ...userInfo.address,
      });
    }
  }, [userInfo, billingAddress, setBillingAddress]);

  useEffect(() => {
    if (
      clickNeedsHandling &&
      accordionIndex === checkoutOptionsIndex &&
      (userInfo || newCustomerOption !== CustomerOption.login)
    ) {
      setAccordionIndex(accountBillingDetailsIndex);
      setClickNeedsHandling(false);
    }
  }, [newCustomerOption, userInfo, clickNeedsHandling, accordionIndex]);

  useEffect(() => {
    if (
      accordionIndex === accountBillingDetailsIndex &&
      billingAddress &&
      userInfo &&
      hasConfirmedBilling
    ) {
      if (shouldGetShipping) {
        setAccordionIndex(shippingDetailsIndex);
      } else {
        setAccordionIndex(paymentIndex);
      }
    }
  }, [
    billingAddress,
    userInfo,
    hasConfirmedBilling,
    accordionIndex,
    shouldGetShipping,
  ]);

  useEffect(() => {
    if (accordionIndex === shippingDetailsIndex && shippingAddress) {
      setAccordionIndex(paymentIndex);
    }
  }, [accordionIndex, shippingAddress]);

  useEffect(() => {
    if (accordionIndex === paymentIndex && localOrderNumber) {
      setAccordionIndex(confirmationIndex);
      setOrderNumber(localOrderNumber);

      TagManager.dataLayer({
        dataLayer: {
          event: 'transaction',
          pagePath: 'checkout',
          pageTitle: 'Checkout - Successful',
          ecommerce: {
            purchase: {
              actionField: {
                id: localOrderNumber,
                affiliation: purchaseURL,
                revenue: total,
                tax: '',
                shipping: '',
                coupon: '',
              },
              products: ItemObject,
            },
          },
        },
      });

      setOrderCompleted(true);
      navigate('/checkout-complete');
    }
  }, [
    ItemObject,
    accordionIndex,
    localOrderNumber,
    navigate,
    purchaseURL,
    setOrderNumber,
    total,
  ]);

  useEffect(() => {
    if (!items.length && !loading) {
      navigate('/');
    }
  }, [items.length, loading, navigate]);

  const isTabEnabled = (index: number): boolean => {
    const hasBillingDetails = !!(
      userInfo &&
      billingAddress &&
      hasConfirmedBilling
    );
    switch (index) {
      case checkoutOptionsIndex:
        return !localOrderNumber;
      case accountBillingDetailsIndex:
        return (
          !!(newCustomerOption !== CustomerOption.login || userInfo) &&
          !localOrderNumber
        );
      case shippingDetailsIndex:
        return hasBillingDetails && shouldGetShipping && !localOrderNumber;
      case paymentIndex:
        return (
          !!(shippingAddress || (hasBillingDetails && !shouldGetShipping)) &&
          !localOrderNumber
        );
      case confirmationIndex:
        return !!localOrderNumber;
      default:
        return false;
    }
  };

  return (
    <>
      <Nav />
      <Head
        title="Checkout"
        link="/checkout"
        keywords="checkout, new account, returning customer"
        twitterUsername="@advancedmedmkt"
      />
      <div
        style={{ maxWidth: '1140px', margin: '0 auto', height: 'max-content' }}
        className={orderCompleted ? 'hidden' : 'block'}
      >
        <p
          {...buttonize(() => navigate(-1))}
          className="text-primary no-underline cursor-pointer uppercase pl-2 text-xs my-2"
          style={{
            fontFamily: 'Raleway, sans-serif',
            fontWeight: 600,
            letterSpacing: '2px',
            width: 'maxContent',
          }}
        >
          Go Back
        </p>
      </div>
      <div
        className="grid flex justify-content-center mx-auto mt-0 mb-4"
        style={{ maxWidth: '1140px' }}
      >
        <div className="col-12 lg:col-8 flex-order-1 lg:flex-order-0">
          <Accordion
            activeIndex={accordionIndex}
            onTabChange={e => {
              setAccordionIndex(e.index);
            }}
          >
            <AccordionTab
              header="Step 1: Checkout Options"
              disabled={!isTabEnabled(0)}
            >
              <CheckoutAccount
                setClickNeedsHandling={setClickNeedsHandling}
                newCustomerOption={newCustomerOption}
                setNewCustomerOption={setNewCustomerOption}
              />
            </AccordionTab>
            <AccordionTab
              header="Step 2: Account & Billing Details"
              disabled={!isTabEnabled(1)}
            >
              <AccountAndBillingForm
                newCustomerOption={newCustomerOption}
                setHasConfirmedBilling={setHasConfirmedBilling}
              />
            </AccordionTab>
            <AccordionTab
              header="Step 3: Shipping Details"
              disabled={!isTabEnabled(2)}
            >
              <ShippingForm />
            </AccordionTab>
            <AccordionTab header="Step 4: Payment" disabled={!isTabEnabled(3)}>
              <CheckoutPayment setOrderNumber={setLocalOrderNumber} />
            </AccordionTab>
            <AccordionTab
              header="Step 5: Confirmation"
              disabled={!isTabEnabled(4)}
            >
              <CheckoutConfirmation />
            </AccordionTab>
          </Accordion>
        </div>
        <CheckoutSummary orderCompleted={orderCompleted} />
      </div>
      <Footer />
    </>
  );
}
